import { type ReactNode, useCallback } from 'react'

import { Form } from '@/components/ui/form'
import { TemplateVariableFieldList } from './template-variable-field-list.molecule'
import type {
  TemplateVariableFormProps,
  TemplateVariableFormValues,
} from './template-variable-form.interface'
import { TemplateVariableFormActions } from './template-variable-form-actions.atom'
import { useTemplateVariableForm } from './use-template-variable-form.hook'

/**
 * Dynamic form organism that renders form fields from parsed template variables.
 * Generates Zod-powered validation, proper error display, and Cancel/Submit actions.
 */
export const TemplateVariableForm: React.FC<TemplateVariableFormProps> = ({
  variableList,
  onSubmit,
  onCancel,
  isDisabled = false,
  submitLabel,
  cancelLabel,
  className,
}): ReactNode => {
  const { form, uniqueVariableList } = useTemplateVariableForm(variableList)

  const handleFormSubmit = useCallback(
    (values: TemplateVariableFormValues) => {
      onSubmit(values)
    },
    [onSubmit]
  )

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className={`template-variable-form space-y-4 ml-1 ${className ?? ''}`}
      >
        <TemplateVariableFieldList
          form={form}
          variableList={uniqueVariableList}
          isDisabled={isDisabled}
        />
        <TemplateVariableFormActions
          onCancel={onCancel}
          isDisabled={isDisabled}
          submitLabel={submitLabel}
          cancelLabel={cancelLabel}
        />
      </form>
    </Form>
  )
}

TemplateVariableForm.displayName = 'TemplateVariableForm'
